<template>
  <v-card class="premium-wrapper rounded-xl elevation-3">

    <v-card-text class="pa-6">
      <!-- MORADA ATUAL -->
      <v-card elevation="0" class="premium-section mb-6 rounded-xl">
        <v-card-title class="px-5 py-4 d-flex align-center">
          <v-icon start color="primary">mdi-home-map-marker</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-medium">Morada</div>
            <div class="text-caption text-grey">
              Dados atuais associados à obra
            </div>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="px-5 py-4">
          <v-row dense>
            <v-col cols="12" md="4" class="mb-2">
              <div class="address-label">País</div>
              <div class="address-value">
                {{ fields.country || '-' }}
              </div>
            </v-col>

            <v-col cols="12" md="8" class="mb-2">
              <div class="address-label">Morada</div>
              <div class="address-value">
                <span v-if="fields.route">{{ fields.route }}</span>
                <span v-if="fields.locality">
                  <span v-if="fields.route">,&nbsp;</span>{{ fields.locality }}
                </span>
                <span v-if="fields.administrative_area_level_2">
                  <span v-if="fields.locality">,&nbsp;</span>{{ fields.administrative_area_level_2 }}
                </span>
                <span v-if="fields.administrative_area_level_1">
                  <span v-if="fields.administrative_area_level_2">,&nbsp;</span>{{ fields.administrative_area_level_1 }}
                </span>
                <span v-if="!fields.route && !fields.locality && !fields.administrative_area_level_2 && !fields.administrative_area_level_1">
                  -
                </span>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- GOOGLE MAPS -->
      <v-card class="premium-section elevation-3 rounded-xl">
        <v-card-title class="px-5 py-4 d-flex align-center">
          <v-icon start color="primary">mdi-map</v-icon>
          <div>
            <div class="text-subtitle-1 font-weight-medium">Localização no Mapa</div>
            <div class="text-caption text-grey">
              Pesquise a morada ou ajuste manualmente o círculo
            </div>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5">
          <!-- PESQUISA -->
          <v-sheet class="mb-4 px-3 py-3 rounded-lg search-panel" elevation="0">
            <v-row align="center" no-gutters>
              <v-col cols="12" md="8" class="pr-md-3 mb-3 mb-md-0">
                <v-menu
                  v-model="searchMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-width="600"
                  min-width="300"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="searchText"
                      v-bind="attrs"
                      v-on="on"
                      ref="searchField"
                      label="Pesquisar morada"
                      prepend-inner-icon="mdi-magnify"
                      clearable
                      density="comfortable"
                      variant="solo"
                      hide-details
                      class="rounded-pill"
                      @input="onSearchInput"
                    />
                  </template>

                  <v-card elevation="4" class="rounded-lg">
                    <v-list density="compact">
                      <v-list-item v-if="suggestionsLoading">
                        <v-list-item-content>
                          <v-list-item-title>A procurar...</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        v-for="(s, index) in suggestions"
                        :key="index"
                        @click="selectSuggestion(s)"
                        class="suggestion-item"
                      >
                        <v-list-item-icon>
                          <v-icon size="small">mdi-map-marker</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            {{ s.formatted_address }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item
                        v-if="!suggestionsLoading && suggestions.length === 0 && searchText.length >= 3"
                      >
                        <v-list-item-content>
                          <v-list-item-title>Nenhuma morada encontrada</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </v-col>

              <v-col cols="12" md="4">
                <v-btn
                  :disabled="!searchText"
                  color="primary"
                  size="large"
                  block
                  class="rounded-pill text-none font-weight-medium"
                  @click="searchAddress"
                >
                  <v-icon start>mdi-magnify</v-icon>
                  Procurar
                </v-btn>
              </v-col>
            </v-row>
          </v-sheet>

          <!-- BOTÕES DE CONTROLO -->
          <v-row class="mb-4" justify="space-between" no-gutters>
            <v-col cols="12" md="4" class="mb-2 mb-md-0" v-if="markers.length > 0">
              <v-btn
                @click="centerMap"
                color="primary"
                variant="tonal"
                block
                class="rounded-pill text-none font-weight-medium"
              >
                <v-icon>mdi-crosshairs-gps</v-icon>Centrar Mapa
              </v-btn>
            </v-col>

            <v-col cols="12" md="4" class="mb-2 mb-md-0" v-if="markers.length === 0">
              <v-btn
                @click="drawCircle"
                color="primary"
                variant="tonal"
                block
                class="rounded-pill text-none font-weight-medium"
                prepend-icon="mdi-draw-pen"
              >
                Definir Localização
              </v-btn>
            </v-col>

            <v-col cols="12" md="4" v-if="removeMarkerButton && markers.length > 0">
              <v-btn
                @click="cleanMarkers"
                color="error"
                variant="tonal"
                block
                class="rounded-pill text-none font-weight-medium"
                prepend-icon="mdi-trash-can"
              >
                Remover
              </v-btn>
            </v-col>
          </v-row>

          <!-- MAPA -->
          <v-row>
            <v-col cols="12">
              <div class="map-container rounded-xl overflow-hidden elevation-3">
                <GmapMap
                  v-if="!loading"
                  :key="gmapReloaded"
                  ref="gmap"
                  :center="center"
                  :options="getOptions"
                  :zoom="zoom"
                  map-type-id="roadmap"
                  style="width: 100%; height: 500px"
                >
                  <GmapCircle
                    v-for="(m, index) in markers"
                    :key="index"
                    :draggable="true"
                    :center="m.position"
                    :radius="55"
                    :visible="true"
                    :options="circleOptions"
                    @center_changed="updateCoordinates"
                    @click="showRemoveMarker(m)"
                  />
                </GmapMap>
              </div>
            </v-col>
          </v-row>

          <!-- BOTÃO GUARDAR -->
          <v-row justify="center" class="mt-6">
            <v-col cols="12" md="auto">
              <v-btn
                :disabled="markers.length === 0"
                @click="openConfirmation"
                color="success"
                size="large"
                class="px-10 rounded-pill text-none font-weight-bold elevation-3"
                prepend-icon="mdi-content-save"
              >
                Guardar Localização
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>

    <!-- CONFIRMAÇÃO -->
    <DialogConfirmation
      v-if="openConfirmationDialog"
      title="Confirme a sua ação"
      type="warning"
      :opened="openConfirmationDialog"
      @on-submit="saveMapMarkers"
      @on-cancel="centerMap(); openConfirmationDialog = false"
    >
      <v-row>
        <v-col cols="12">
          Tem a certeza que a localização que deseja está correta?
        </v-col>
      </v-row>

      <v-row v-if="markers.length > 0" class="mt-2">
        <v-col cols="12" md="4" class="font-weight-bold">País</v-col>
        <v-col cols="12" md="8">{{ fields.country }}</v-col>

        <v-col cols="12" md="4" class="font-weight-bold">Morada</v-col>
        <v-col cols="12" md="8">
          <p>{{ fields.route }}</p>
          <p>{{ fields.locality }}</p>
          <p>{{ fields.administrative_area_level_2 }}</p>
          <p>{{ fields.administrative_area_level_1 }}</p>
        </v-col>
      </v-row>
    </DialogConfirmation>

    <SuccessSnackBar v-model="modalSuccess.show" :message="modalSuccess.message" />
  </v-card>
</template>


<script>
/* global google */
import Work from '@/api/Works.js'
import SuccessSnackBar from '@/components/UI/Snackbars/SuccessSnackbar'
import DialogConfirmation from '@/components/UI/DialogConfirmation.vue'

export default {
  name: 'WorkLocalization',
  components: { SuccessSnackBar, DialogConfirmation },
  props: {
    id: {
      type: String,
      required: true, // ID da obra
    },
  },
  data () {
    return {
      openConfirmationDialog: false,
      gmapReloaded: 0,
      loading: true,
      zoom: 15,
      center: { lat: 0, lng: 0 },
      markers: [],
      markerToRemove: null,
      removeMarkerButton: false,

      // Autocomplete "manual"
      searchText: '',
      searchMenu: false,
      searchTimeout: null,
      suggestions: [],
      suggestionsLoading: false,

      modalSuccess: { show: false, message: 'Localização gravada com sucesso.' },
      fields: {
        street_number: null,
        administrative_area_level_1: null,
        administrative_area_level_2: null,
        country: null,
        locality: null,
        route: null,
        lat: null,
        lng: null,
      },
    }
  },
  async mounted () {
    try {
      const resp = await Work.getLocal(this.id)
      if (resp && resp.data) {
        const loc = resp.data

        // converter para números
        const lat = parseFloat(loc.lat)
        const lng = parseFloat(loc.lng)

        this.fields = {
          street_number: loc.street_number,
          administrative_area_level_1: loc.administrative_area_level_1,
          administrative_area_level_2: loc.administrative_area_level_2,
          country: loc.country,
          locality: loc.locality,
          route: loc.route,
          lat,
          lng,
        }

        if (!isNaN(lat) && !isNaN(lng)) {
          this.center = { lat, lng }
          this.markers.push({
            position: { lat, lng },
            radius: 55,
          })
        } else {
          this.geolocate()
        }
      } else {
        this.geolocate()
      }
    } catch (e) {
      console.error('Erro ao obter localização da obra:', e)
      this.geolocate()
    } finally {
      this.loading = false
    }
  },
  methods: {
    openConfirmation () {
      if (this.markers.length > 0) {
        this.codeLatLng(this.markers[0].position.lat, this.markers[0].position.lng)
      }
      this.openConfirmationDialog = true
    },
    centerMap () {
      // força o re-render do mapa mantendo o center
      this.gmapReloaded++
    },
    updateCoordinates (location) {
      if (!this.markers.length) return
      this.markers[0].position.lat = location.lat()
      this.markers[0].position.lng = location.lng()
    },
    async saveMapMarkers () {
      let data = { ...this.fields }

      if (this.markers.length !== 0) {
        await this.codeLatLng(this.markers[0].position.lat, this.markers[0].position.lng)
        data = {
          ...this.fields,
          lat: this.markers[0].position.lat,
          lng: this.markers[0].position.lng,
        }
      } else {
        data = { work_id: null }
      }

      data.work_id = this.id

      try {
        await Work.setLocal(data)
        this.modalSuccess.show = true
        this.openConfirmationDialog = false
      } catch (err) {
        console.error('Erro ao gravar localização:', err)
      }
    },
    cleanMarkers () {
      this.markers = []
      this.removeMarkerButton = false
    },
    drawCircle () {
      const map = this.$refs.gmap?.$mapObject
      this.center = {
        lat: map?.getCenter()?.lat?.() ?? this.center.lat,
        lng: map?.getCenter()?.lng?.() ?? this.center.lng,
      }
      this.drawable(this.center.lat, this.center.lng)
    },
    drawable (lat, lng) {
      this.cleanMarkers()
      this.markers.push({ position: { lat, lng }, radius: 55 })
    },
    showRemoveMarker (m) {
      this.markerToRemove = m
      this.removeMarkerButton = true
    },
    geolocate () {
      const defaultCenter = { lat: 39.5, lng: -8.0 } // centro de PT

      if (!navigator.geolocation) {
        this.center = defaultCenter
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
        },
        (err) => {
          console.info('Geolocate falhou, usar default:', err)
          this.center = defaultCenter
        }
      )
    },
    async codeLatLng (lat, lng) {
      const g = window.google
      if (!g?.maps) return

      const geocoder = new g.maps.Geocoder()
      const latlng = new g.maps.LatLng(lat, lng)

      try {
        const { results, status } = await new Promise((resolve) => {
          geocoder.geocode({ latLng: latlng }, (res, stat) => {
            if (stat === g.maps.GeocoderStatus.OK) {
              resolve({ results: res, status: stat })
            } else {
              resolve({ results: [], status: stat })
            }
          })
        })

        if (status === g.maps.GeocoderStatus.OK && results[0]) {
          // reset
          this.fields.locality = null
          this.fields.administrative_area_level_1 = null
          this.fields.administrative_area_level_2 = null
          this.fields.route = null
          this.fields.street_number = null
          this.fields.country = null

          for (const component of results[0].address_components) {
            const t = component.types[0]
            switch (t) {
              case 'locality':
                this.fields.locality = component.long_name
                break
              case 'administrative_area_level_1':
                this.fields.administrative_area_level_1 = component.long_name
                break
              case 'administrative_area_level_2':
                this.fields.administrative_area_level_2 = component.long_name
                break
              case 'route':
                this.fields.route = component.long_name
                break
              case 'street_number':
                this.fields.street_number = component.long_name
                break
              case 'country':
                this.fields.country = component.long_name
                break
            }
          }
        }
      } catch (e) {
        console.error('Geocode falhou:', e)
      }
    },

    // ---------- AUTOCOMPLETE "MANUAL" ----------

    onSearchInput () {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)

      if (!this.searchText || this.searchText.length < 3) {
        this.suggestions = []
        this.suggestionsLoading = false
        this.searchMenu = false
        return
      }

      this.searchTimeout = setTimeout(() => {
        this.fetchSuggestions()
      }, 400) // debounce ~400ms
    },

    async fetchSuggestions () {
      const g = window.google
      if (!g?.maps) return

      this.suggestionsLoading = true
      this.searchMenu = true

      const geocoder = new g.maps.Geocoder()

      try {
        const { results, status } = await new Promise((resolve) => {
          geocoder.geocode({ address: this.searchText }, (res, stat) => {
            if (stat === g.maps.GeocoderStatus.OK) {
              resolve({ results: res, status: stat })
            } else {
              resolve({ results: [], status: stat })
            }
          })
        })

        if (status === g.maps.GeocoderStatus.OK && results.length > 0) {
          this.suggestions = results // guardamos o objeto todo
        } else {
          this.suggestions = []
        }
      } catch (e) {
        console.error('Erro ao obter sugestões:', e)
        this.suggestions = []
      } finally {
        this.suggestionsLoading = false
      }
    },

    async selectSuggestion (s) {
      this.searchText = s.formatted_address
      this.searchMenu = false

      if (!s.geometry) return

      const loc = s.geometry.location
      const lat = loc.lat()
      const lng = loc.lng()

      this.center = { lat, lng }
      this.drawable(lat, lng)
      await this.codeLatLng(lat, lng)
    },

    async searchAddress () {
      // botão "Procurar" força uma pesquisa e selecção do 1º resultado
      await this.fetchSuggestions()
      if (this.suggestions.length > 0) {
        await this.selectSuggestion(this.suggestions[0])
      }
    },
  },
  computed: {
    getOptions () {
      return {
        clickableIcons: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
          { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
          {
            featureType: 'poi',
            elementType: 'geometry',
            stylers: [{ color: '#eeeeee' }],
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#ffffff' }],
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#c9c9c9' }],
          },
        ],
      }
    },
    circleOptions () {
      return {
        strokeColor: '#1976d2',
        strokeOpacity: 0.8,
        strokeWeight: 3,
        fillColor: '#1976d2',
        fillOpacity: 0.35,
      }
    },
  },
}
</script>

<style scoped>
.premium-wrapper {
  background: linear-gradient(135deg, #fafafa 0%, #f3f7ff 100%);
}


.premium-section {
  background-color: #ffffff;
}

/* Morada */
.address-label {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
  margin-bottom: 4px;
}

.address-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #424242;
}

/* Caixa de pesquisa */
.search-panel {
  background: #f5f7fb;
  border: 1px solid rgba(25, 118, 210, 0.08);
}

.suggestion-item {
  cursor: pointer;
}

/* Mapa */
.map-container {
  width: 100%;
  height: 500px;
  border-radius: 18px;
}
</style>
